import { IAm, MemberBenefit, Membership, Subscription } from '@shared/types';
import { PublicCourse, PublicEducator } from '@cademy-package/sdk-component-types';
import Link from 'next/link';
import { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { useCourseLink } from '../../../../hooks/useCourseLink';
import { useLoginLink } from '../../../../hooks/useLoginLink';
import { useMinisiteLink } from '../../../../hooks/useMinisiteLink';
import { useEducatorProfileLinks } from '../../../../hooks/useEducatorProfileLinks';
import { useIsMarketplace } from '../../../../contexts/AppContext/useIsMarketplace';
import { useIsEmbed } from '../../../../contexts/AppContext/useIsEmbed';
import styles from './styles.module.scss';
import Card from '../../../Card';
import { getMembershipCheckoutLink } from '../../../../services/getMembershipCheckoutLink';
import useEmbedExternalLinkProps from '../../../../hooks/useEmbedExternalLinkProps';
import ButtonLink from '../../../ButtonLink';
import { PricingLabel } from '../../../../app/components/PricingLabel';

const getToken = () => {
    if (typeof window !== 'undefined') {
        return window.localStorage.getItem('cademyStudentToken');
    }

    return null;
};

export const useIsMembershipRequired = (
    iam: IAm | null,
    course: PublicCourse,
    memberships: Membership[]
) => {
    const firstRun = useRef(true);
    const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);

    const courseRequiredMemberships = useMemo(
        () =>
            memberships.reduce((requiredMemberships: Membership[], membership: Membership) => {
                const benefitsRelatedToCourse = membership.benefits.filter((benefit) =>
                    benefit.course_refs.includes(course._id)
                );

                if (benefitsRelatedToCourse.some((benefit) => benefit.type === 'members-only')) {
                    return requiredMemberships.concat(membership);
                }
                return requiredMemberships;
            }, []),
        [course._id, memberships]
    );

    useEffect(() => {
        if (
            iam &&
            iam.authenticated &&
            courseRequiredMemberships.length > 0 &&
            firstRun.current === true
        ) {
            firstRun.current = false;

            const token = getToken();
            const Authorization: { Authorization: string } | {} = token
                ? { Authorization: `Bearer ${token}` }
                : {};

            fetch(`${process.env.NEXT_PUBLIC_CADEMY_API_URL}/subscriptions`, {
                headers: {
                    ...Authorization,
                },
                credentials: 'include',
            })
                .then(async (res) => {
                    const json = await res.json();
                    setSubscriptions(json);
                })
                .catch((error) => {
                    console.error(error);
                });
        }
    }, [iam, courseRequiredMemberships]);

    const subscriptionIsValid = useCallback((subscription?: Subscription) => {
        if (!subscription) return false;

        const now = Math.round(Date.now() / 1000);
        return subscription.starts_at < now && subscription.expires_at > now && subscription.active;
    }, []);

    const subscriptionMembershipIds = useMemo(
        () => subscriptions.filter(subscriptionIsValid).map((sub) => sub.membership_ref),
        [subscriptions, subscriptionIsValid]
    );

    const studentHasRequiredMembership = useMemo(
        () =>
            courseRequiredMemberships.reduce((has, membership) => {
                if (subscriptionMembershipIds.includes(membership._id)) return true;
                return has;
            }, false),
        [courseRequiredMemberships, subscriptionMembershipIds]
    );

    return {
        requiredMemberships: courseRequiredMemberships,
        studentHasRequiredMembership,
        membershipRequired: courseRequiredMemberships.length > 0 && !studentHasRequiredMembership,
    };
};

export const MembershipRequiredWarning = ({
    iam,
    course,
    membershipRequired,
    requiredMemberships,
    educator,
}: {
    iam: IAm;
    course: PublicCourse;
    membershipRequired: boolean;
    requiredMemberships: Membership[];
    educator: PublicEducator;
}) => {
    const loginLink = useLoginLink();
    const { membershipsLink } = useEducatorProfileLinks(educator.slug);
    const isMarketplace = useIsMarketplace();
    const isEmbed = useIsEmbed();
    const embedExternalLinkProps = useEmbedExternalLinkProps(isEmbed);
    const minisiteMembershipCheckoutLink = useMinisiteLink(educator.slug, '/membership-checkout');
    const minisiteMembershipsLink = useMinisiteLink(educator.slug, '/memberships');

    if (requiredMemberships.length === 0 || !membershipRequired) return null;

    const bookEnrolKeyword = course.type === 'on-demand' ? 'enrol' : 'book';

    if (requiredMemberships.length === 1) {
        const membership = requiredMemberships[0];
        const membershipCheckoutLink: string = getMembershipCheckoutLink(
            membership._id,
            course._id,
            isMarketplace,
            minisiteMembershipCheckoutLink
        );
        return (
            <div className={styles.membershipRequiredContainer}>
                <strong>Subscribe before you can {bookEnrolKeyword}:</strong>
                <ButtonLink
                    href={membershipCheckoutLink}
                    {...embedExternalLinkProps}
                    preset="primary"
                >
                    {membership.recurring === 'never' ? (
                        <>
                            Join for &nbsp;
                            <PricingLabel price={membership.price} currency={membership.currency} />
                        </>
                    ) : (
                        <>
                            Subscribe for &nbsp;
                            <PricingLabel
                                price={membership.price}
                                currency={membership.currency}
                            />/{membership.recurring}
                        </>
                    )}
                </ButtonLink>
                {iam && iam.authenticated == false ? (
                    <p>
                        <Link href={loginLink} {...embedExternalLinkProps}>
                            Log in
                        </Link>{' '}
                        if you have a subscription
                    </p>
                ) : null}
            </div>
        );
    }

    return (
        <div className={styles.membershipRequired}>
            <p>You need a membership to book this course.</p>
            {iam && iam.authenticated ? (
                <p>
                    <Link
                        href={`${isEmbed ? minisiteMembershipsLink : membershipsLink}?course_slug=${course.slug}&marketplace=${isMarketplace}`}
                        {...embedExternalLinkProps}
                    >
                        Get a membership
                    </Link>{' '}
                    to continue.
                </p>
            ) : (
                <p>
                    <Link
                        href={`${isEmbed ? minisiteMembershipsLink : membershipsLink}?course_slug=${course.slug}&marketplace=${isMarketplace}`}
                        {...embedExternalLinkProps}
                    >
                        Get a membership
                    </Link>{' '}
                    or{' '}
                    <Link href={loginLink} {...embedExternalLinkProps}>
                        log in
                    </Link>{' '}
                    if you have one.
                </p>
            )}
        </div>
    );
};

export const MembershipFreeDiscountedAccessMessage = ({
    iam,
    course,
    memberships,
    membershipRequired,
    educator,
}: {
    iam: IAm;
    course: PublicCourse;
    memberships: Membership[];
    membershipRequired: boolean;
    educator: PublicEducator;
}) => {
    const { membershipsLink: contextMembershipsLink } = useEducatorProfileLinks(educator.slug);
    const minisiteMembershipLink = useMinisiteLink(educator.slug, '/memberships');
    const minisiteCourseLink = useMinisiteLink(educator.slug, `/${course.slug}`);
    const contextCourseLink = useCourseLink(educator.slug, course.slug);
    const isEmbed = useIsEmbed();

    const courseLink = useMemo(() => {
        return isEmbed === true ? minisiteCourseLink : contextCourseLink.href;
    }, [isEmbed, contextCourseLink, minisiteCourseLink]);

    const membershipsLink = useMemo(() => {
        if (!contextMembershipsLink) {
            return minisiteMembershipLink;
        }
        return isEmbed === true ? minisiteMembershipLink : contextMembershipsLink;
    }, [isEmbed, contextMembershipsLink, minisiteMembershipLink]);

    const findRelevantMemberships = useCallback(
        (benefitType: MemberBenefit['type']) => {
            return memberships.filter((membership) => {
                return membership.benefits.some(
                    (benefit) =>
                        benefit.course_refs.includes(course._id) && benefit.type === benefitType
                );
            });
        },
        [course, memberships]
    );

    const freeMemberships = useMemo(
        () => findRelevantMemberships('free-entry'),
        [findRelevantMemberships]
    );
    const discountedMemberships = useMemo(
        () => findRelevantMemberships('discounted-entry'),
        [findRelevantMemberships]
    );

    const ctaLink = useMemo(() => {
        const target = isEmbed ? '_blank' : '_self';
        const loginUrl = `/login?redirect=${courseLink}`;

        if (iam && iam.authenticated) {
            return <Link href={membershipsLink}>View memberships</Link>;
        }

        return (
            <span>
                <Link href={loginUrl} target={target} rel="nofollow">
                    Log in
                </Link>{' '}
                if you have a membership or{' '}
                <Link href={membershipsLink} target={target}>
                    view memberships
                </Link>{' '}
                to find out more.
            </span>
        );
    }, [iam, courseLink, membershipsLink, isEmbed]);

    const ctaMessage = useMemo(() => {
        if (discountedMemberships.length && freeMemberships.length) {
            return (
                <p>
                    Discounted &amp; Free access available with a membership.<br></br>
                    {ctaLink}
                </p>
            );
        } else if (discountedMemberships.length) {
            return (
                <p>
                    Discounted access available with a membership.<br></br>
                    {ctaLink}
                </p>
            );
        } else if (freeMemberships.length) {
            return (
                <p>
                    Free access available with a membership.<br></br>
                    {ctaLink}
                </p>
            );
        }
        return null;
    }, [discountedMemberships, freeMemberships, ctaLink]);

    if (membershipRequired === true || ctaMessage === null) return null;

    return (
        <Card className={styles.membershipAccessMessage}>
            <span>{ctaMessage}</span>
        </Card>
    );
};

export default MembershipRequiredWarning;
